import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import { FlyerAnalysis } from "@/components/flyers/FlyerAnalysis";
import { MatchTab } from "@/components/flyers/MatchTab";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fdate } from "@/lib/api";

const EditMeta = ({ flyer, open, onClose, onSaved }) => {
  const [m, setM] = useState({ name: flyer.name, start: flyer.start, end: flyer.end });
  useEffect(() => setM({ name: flyer.name, start: flyer.start, end: flyer.end }), [flyer, open]);
  const save = async () => { try { onSaved((await api.put(`/flyers/${flyer.id}`, m)).data); toast.success("Volantino aggiornato"); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="flyer-edit-dialog">
        <DialogHeader><DialogTitle>Modifica volantino</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input data-testid="flyer-edit-name" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3"><DatePicker testId="flyer-edit-start" value={m.start} max={m.end} onChange={(v) => setM({ ...m, start: v })} /><DatePicker testId="flyer-edit-end" value={m.end} min={m.start} onChange={(v) => setM({ ...m, end: v })} /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="flyer-edit-save" disabled={!m.name || !m.start || !m.end} className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function FlyerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [flyer, setFlyer] = useState(null);
  const [edit, setEdit] = useState(false);
  const canWrite = user?.permissions?.flyers === "write";
  const tab = params.get("tab") === "prodotti" ? "prodotti" : "analisi";
  useEffect(() => { api.get(`/flyers/${id}`).then((r) => setFlyer(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/volantini-promo"); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!flyer) return null;
  const todo = flyer.items.filter((i) => i.match === "descr" || i.match === "none").length;
  return (
    <div data-testid="flyer-detail-page">
      <button type="button" className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="flyer-back" onClick={() => nav("/volantini-promo")}><ArrowLeft className="h-4 w-4" />Tutti i volantini</button>
      <PageHeader title={flyer.name} subtitle={`Valido dal ${fdate(flyer.start)} al ${fdate(flyer.end)} · ${flyer.items.length} prodotti`}>
        {canWrite && <Button variant="outline" data-testid="flyer-edit-button" onClick={() => setEdit(true)}><Pencil className="mr-1 h-4 w-4" />Modifica</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={(v) => setParams(v === "prodotti" ? { tab: v } : {}, { replace: true })}>
        <TabsList className="mb-6"><TabsTrigger value="analisi" data-testid="flyer-tab-analysis">Analisi</TabsTrigger>
          <TabsTrigger value="prodotti" data-testid="flyer-tab-products">Prodotti e abbinamenti{todo > 0 && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-700">{todo}</span>}</TabsTrigger></TabsList>
        <TabsContent value="analisi">{tab === "analisi" && <FlyerAnalysis flyer={flyer} />}</TabsContent>
        <TabsContent value="prodotti"><MatchTab flyer={flyer} setFlyer={setFlyer} canWrite={canWrite} /></TabsContent>
      </Tabs>
      <EditMeta flyer={flyer} open={edit} onClose={() => setEdit(false)} onSaved={setFlyer} />
    </div>
  );
}
